import xeComVisuals from "../../media/xeComVisuals";

const xeCom = {
  "id": "xe-com",
  "slug": "xe-com",
  "category": "Applications",
  "navTitle": "Growing XE.com",
  "title": "Growing XE.com to Over 3 Million Daily Users",
  "introduction": [
    "Over more than a decade at XE, I helped evolve the website from a collection of useful currency tools into one of the world's most visited financial websites. Rather than relying on major redesigns, we continuously improved the experience through user research, product enhancements, accessibility, and search optimization.",
    "During that time, daily unique users grew from approximately 1.8 million to more than 3 million, placing XE among the world's most visited websites."
  ],
  "challenge": [
    {
      "title": "Growing in a competitive market",
      "paragraphs": [
        "Financial tools were becoming increasingly available online. To continue growing, XE needed to offer an experience that was easier to use, easier to discover, and more valuable than competing services."
      ]
    },
    {
      "title": "Balancing new and existing users",
      "paragraphs": [
        "Millions of people already relied on XE's tools every day. Improvements needed to help first-time visitors without disrupting the workflows trusted by long-time users."
      ]
    },
    {
      "title": "Modernizing an established platform",
      "paragraphs": [
        "As the website expanded, navigation, content, accessibility, and technical performance all needed to evolve without sacrificing the speed and reliability users expected."
      ]
    },
    {
      "title": "Supporting business growth",
      "paragraphs": [
        "The website also needed to introduce new products and services, including international money transfer, while preserving XE's reputation as a trusted source for currency information."
      ]
    }
  ],
  "process": [
    {
      "title": "Continuously improving the user experience",
      "paragraphs": [
        "I treated the website as an evolving product, making regular improvements based on research, analytics, and customer feedback rather than relying on large redesigns."
      ],
      "bullets": [
        "Simplified navigation and page layouts to help people find information more quickly.",
        "Improved key customer journeys while preserving familiar workflows for existing users.",
        "Used analytics, usability testing, and customer feedback to identify friction and opportunities.",
        "Ran A/B tests to validate design decisions before broader rollout.",
        "Refined the experience through regular iterations rather than infrequent redesigns."
      ],
      "closing": "This allowed the website to evolve steadily while maintaining the trust of millions of existing users."
    },
    {
      "title": "Expanding value and discoverability",
      "paragraphs": [
        "Growth came from making XE more useful while ensuring customers could easily find the information they were looking for."
      ],
      "bullets": [
        "Worked with marketing and content teams to align new content with customer needs and search behaviour.",
        "Improved technical SEO through responsive design, site performance, semantic markup, and cleaner information architecture.",
        "Treated accessibility improvements as enhancements to both usability and discoverability.",
        "Expanded educational content, currency information, and supporting tools that encouraged repeat visits.",
        "Supported the introduction of additional financial services without compromising the site's core purpose."
      ],
      "closing": "Rather than chasing search rankings alone, the focus remained on creating content and experiences people genuinely found valuable."
    },
    {
      "title": "Using evidence to guide decisions",
      "paragraphs": [
        "Analytics became an ongoing decision-making tool rather than a reporting exercise."
      ],
      "bullets": [
        "Monitored customer behaviour to understand where people succeeded, struggled, or left the site.",
        "Evaluated new ideas through testing before broader implementation.",
        "Prioritized improvements based on customer impact, business value, and technical effort.",
        "Used performance data to refine navigation, content, page layouts, and conversion opportunities.",
        "Continued measuring results after release so improvements could be adjusted over time."
      ],
      "closing": "This created a cycle of continuous improvement instead of treating launches as finished work."
    }
  ],
  "outcomes": [
    {
      "title": "Sustained audience growth",
      "description": "Daily unique users increased from approximately 1.8 million to more than 3 million during my time at XE."
    },
    {
      "title": "Stronger customer engagement",
      "description": "Improvements to navigation, usability, content, and performance contributed to longer sessions, lower bounce rates, and greater customer retention."
    },
    {
      "title": "Increased business value",
      "description": "The website became a stronger entry point into XE's broader financial ecosystem while maintaining its reputation as a trusted source for currency information."
    },
    {
      "title": "A foundation for continued growth",
      "description": "The combination of user-centred design, accessibility, content strategy, and ongoing optimization created a sustainable approach to growing the platform over many years."
    }
  ],
  "reflection": [
    "Growing the website wasn't the result of one successful redesign or a single SEO initiative. It came from continually removing friction, understanding what customers needed next, and improving the experience one step at a time.",
    "Over time, those incremental improvements added up. Better navigation, more useful content, stronger accessibility, and thoughtful product decisions reinforced one another and helped the platform continue growing while serving millions of people every day."
  ],
  "media": {
    "blocks": [
      {
        "id": "xe-com-responsive-evolution",
        "type": "carousel",
        "placement": "afterIntro",
        "items": xeComVisuals,
        "layout": "default",
        "options": {
          "frameMaxHeight": "min(58vh, 680px)"
        }
      }
    ]
  }
};

export default xeCom;
